import React, { FC } from "react";
import {
    ArticleTitle,
    TitleBackplate,
    TitleHead,
    TitleText,
} from "../../components/articles/header/style";
import { Main } from "../../components/articles";
import {
    ArticleLayout,
    ArticleLink,
    ArticleText,
    AuthorPubDate,
    Content,
    ContentSubTitle,
    ContentTitle,
    ArticleSpace,
    FirstArticleImage,
    Header,
} from "../../components/articles/article/style";
import {
    Facebook,
    LinkedIn,
    RightContent,
    Share,
    ShareHeader,
    ShareSocialList,
    StickyWrapper,
    Telegram,
} from "../../components/articles/right/style";
import LearnMore from "../../components/learn_more";

const SecondArticlePage: FC = () => {
    return (
        <Main>
            <ArticleTitle>
                <TitleBackplate imagePath='/articles/decarbonization/article.svg' />
                <TitleText>
                    <TitleHead>
                        Decarbonization, sustainability and ESG. Why does the financial sector need
                        this?
                    </TitleHead>
                </TitleText>
            </ArticleTitle>
            <ArticleSpace>
                <ArticleLayout>
                    <Header>
                        <AuthorPubDate>June 05, 2022</AuthorPubDate>
                    </Header>
                    <Content>
                        <ContentTitle>It{"'"}s all about investments</ContentTitle>
                        <ArticleText>
                            There is a popular misconception that when it comes to the financial
                            sector, it’s all about paper and electricity. Unfortunately, this is not
                            the case. While direct emissions for investors are generally low, their
                            financed emissions are 700 times higher than operational emissions.
                            Moreover, CDP{" "}
                            <ArticleLink
                                href='https://www.cdp.net/en/articles/media/finance-sectors-funded-emissions-over-700-times-greater-than-its-own'
                                rel='noopener noreferrer'
                            >
                                reports
                            </ArticleLink>{" "}
                            that only 25% of financial institutions report their portfolio
                            emissions.
                        </ArticleText>
                        <ContentTitle>
                            What is Environmental, Social, and Governance (ESG) Investing?
                        </ContentTitle>
                        <ArticleText>
                            Socially responsible investors utilize an international set of criteria
                            for a company{"'"}s conduct known as Environmental, Social, and
                            Governance (ESG) Investing to evaluate possible investments. <br />{" "}
                            <br />
                            ESG Investing framework is used by financial institutions to evaluate
                            potential and current investments and to motivate key stakeholders (both
                            investees and investors) to behave ethically going forward. As a result,
                            investing in ESG-compliant companies has become the priority for many
                            funds, brokers, and private investors. By doing so, investors can, to a
                            certain degree, mitigate the risk of holding companies that engage in
                            unsafe or unethical operations, hence minimizing regulatory scrutiny and
                            potential monetary damage.
                        </ArticleText>
                        <ContentSubTitle>ESG Explained</ContentSubTitle>
                        <ArticleText>
                            Corporate climate policy, energy use, waste disposal, pollution,
                            preservation of natural resources, and the endangerment of animals are
                            just a few examples of <b>environmental</b> challenges that we face
                            today.
                            <br /> <br />
                            The company{"'"}s relations with internal and external stakeholders are
                            evaluated through <b>social</b> aspects. It is focused on promoting
                            ethical and socially conscious themes, including diversity, inclusion,
                            cultural and social justice, and corporate ethics, and fighting against
                            racial, gender, and sexual discrimination. It also addresses the
                            employees’ health and safety.
                            <br /> <br />
                            <b>Governance</b> standards guarantee that a company employs correct and
                            transparent accounting methods, selects its leadership with integrity
                            and diversity, and is honest with stakeholders. When a company
                            implements ESG principles, its corporate strategy focuses on these three
                            pillars. Taking measures to reduce wastage, pollution, and CO2
                            generation becomes a priority. Diversity and inclusivity also become
                            important, from entry-level positions to board of directors. ESG may be
                            expensive and time-consuming to implement, but it can also pay off for
                            those who see it through in the long run.
                            <br /> <br />
                            One of the most recognized international frameworks for understanding
                            climate risks for financial institutions is Task Force on
                            Climate-related Financial Disclosures (TCFD). Introduced by the
                            Financial Stability Board in response to the G20 Finance Ministers and
                            Central Bank Governors asking to review how financial institutions could
                            better adapt to the climate risks, TCFD recommendations are now used by
                            such global financial centers as Singapore.
                        </ArticleText>
                        <ContentTitle>
                            Sustainability is imperative to investment vehicles
                        </ContentTitle>
                        <ArticleText>
                            A total of $130T in private assets are{" "}
                            <ArticleLink href='http://www.lonk.ru' rel='noopener noreferrer'>
                                pledged
                            </ArticleLink>{" "}
                            to global decarbonization by the Glasgow Financial Alliance for Net Zero
                            (GFANZ).
                            <br /> <br />
                            According to Mark Carney, a former governor of the Bank of England and
                            co-founder of GFANZ, the difficulty is to {'"'}transform this into a
                            very, very powerful virtuous circle.{'"'} The money is available, but it
                            needs to be used for net zero-aligned initiatives and mechanisms that
                            are scaling and transparent. The main instrument for forming that
                            imperative is investments in ESG-relevant companies.
                            <br /> <br />
                            As investors are learning more about ESG issues, there has been a
                            substantial rise in the amount of capital flowing into funds that
                            promote sustainable investing. The Rising monetary inflow into
                            sustainable investing is not regional phenomenon but a global trend.
                        </ArticleText>
                        <ContentTitle>Sustainable Investment and Venture Capital</ContentTitle>
                        <ArticleText>
                            Venture capital is naturally well-suited for sustainable investment
                            compared to other funding sources.
                            <br /> <br />
                            According to the 2020 Global Sustainable Investment Review, a biannual
                            report released by the Global Sustainable Investment Alliance (GSIA),
                            the total sustainable investing assets in the five major global markets,
                            including Europe, the United States, Canada, Japan, and Australasia
                            stood at USD$ 35.3 trillion at the start of 2020, a 55% increase from
                            four years earlier in 2016.
                            <br /> <br />
                            The graphic below shows the rise in total sustainable investing assets
                            from 2012 to 2020 in the five major markets covered by the GSIA{"'"}s
                            biannual reports. The total quantity of sustainable investing assets and
                            their percentage of all professionally managed assets are on a clear
                            upward trend.
                            <br /> <br />
                            VC is uniquely compatible with the needs of sustainable projects. VC
                            funds typically have a long lock-in period, which aligns well with the
                            time horizon required for companies in the sustainability space to
                            mature and commercialize cutting-edge research. In addition to the
                            financial contribution, VC funds can provide their portfolio companies
                            with technical, management, and government expertise and connections in
                            the industry.
                            <br /> <br />
                            As a result, VC fund managers frequently get involved directly in the
                            corporate governance of their portfolio companies and help them move
                            towards sustainability. This gives business owners additional motivation
                            to run their organizations well in order to reclaim their control rights
                            (Lin Lin, 2022).
                            <br /> <br />
                            <FirstArticleImage
                                src={`${process.env.PUBLIC_URL}/articles/decarb-illustration.svg`}
                            />
                        </ArticleText>
                        <ContentTitle>
                            From theory to practice: a case study in venture capital
                        </ContentTitle>
                        <ArticleText>
                            With our cutting-edge product for the financial industry Offsetted
                            Finance, we empower investment vehicles to embed sustainability in their
                            daily operations and integrate this approach into their portfolio from
                            day one.
                            <br /> <br />
                            With a Singapore-based venture capital firm Atlas Ventures, we have been
                            able to perform ESG monitoring for the fund and its portfolio companies
                            in alignment with the TCFD framework and IRIS+ metrics.
                            <br /> <br />
                            <b>
                                «We struggled to navigate the complex landscape of various climate
                                standards and regulations and couldn{"'"}t believe how fast we{"'"}
                                ve gotten up to speed with Offsetted»
                            </b>
                            , - says CEO of Atlas Ventures Maxim Shkvaruk.{" "}
                            <b>
                                «When we first showcased how the ESG performance dashboards could
                                look to our portfolio, they were surprised how painless and time and
                                cost-efficient this process could be.»
                            </b>
                            <br /> <br />
                        </ArticleText>
                    </Content>
                </ArticleLayout>
                <LearnMore />
            </ArticleSpace>
            <RightContent>
                <StickyWrapper>
                    <Share>
                        <ShareHeader>Share the article</ShareHeader>
                        <ShareSocialList>
                            <Facebook
                                href='https://facebook.com/sharer/sharer.php?u=https://offsetted.com/decarbonization'
                                target='_blank'
                                rel='noopener noreferrer'
                            />
                            <LinkedIn
                                href='https://www.linkedin.com/shareArticle?url=https%3A//offsetted.com/decarbonization'
                                target='_blank'
                                rel='noopener noreferrer'
                            />
                            <Telegram
                                rel='noopener noreferrer'
                                target='_blank'
                                href='https://telegram.me/share/url?url=https://offsetted.com/decarbonization'
                            />
                        </ShareSocialList>
                    </Share>
                </StickyWrapper>
            </RightContent>
        </Main>
    );
};

export default SecondArticlePage;
